/**
 * Client-side mirror of Offers::ApplyPresentationRules (ADR-005).
 * Keep semantics aligned with API — PDF uses the Ruby version.
 *
 * Extra FE pass (document settings): include_notes, show_fields, item: hide.
 * API normalize persists those keys; RenderPdf is owned by offers-export.
 */

import {
  itemNodeKey,
  notesIncluded,
  normalizePresentationRules,
} from "./presentation-rules-shape";

function isGroupRow(item) {
  const type = item?.item_type || item?.type;
  return type === "group" || item?.group_row === true;
}

function read(item, key) {
  return item?.[key];
}

function visible(item) {
  if (item?.delete) return false;
  if (item?.visible_to_customer === false) return false;
  if (item?.pricing_role === "excluded") return false;
  return true;
}

function lineAmount(item) {
  const qty = Number(item?.quantity) || 0;
  const price = Number(item?.sales_price) || 0;
  const surcharge = Math.max(0, Number(item?.surcharge) || 0);
  const discount = Math.max(0, Number(item?.discount) || 0);
  let amount = qty * price;
  amount += (amount * surcharge) / 100;
  amount -= (amount * discount) / 100;
  return amount;
}

function normalizeRules(rules) {
  return normalizePresentationRules(rules);
}

function nodeKey(item) {
  if (item?.calculation_id != null) return `calc:${item.calculation_id}`;
  if (item?.offer_group_id != null) return `group:${item.offer_group_id}`;
  return null;
}

function modeFor(rules, item, rootSet) {
  const key = nodeKey(item);
  if (!key) return "show_children";
  const hit = rules.rules.find((rule) => rule.node_key === key);
  if (hit?.mode) return hit.mode;
  const calcId = Number(item?.calculation_id);
  const isRoot =
    key.startsWith("calc:") &&
    Number.isFinite(calcId) &&
    (rootSet.size === 0 || rootSet.has(calcId));
  if (key.startsWith("calc:") && !isRoot) {
    return rules.defaults.nested_calculation;
  }
  return rules.defaults.group;
}

function buildBlocks(items, rootIds) {
  const rootSet = new Set((rootIds || []).map((id) => Number(id)));
  const flat = [];
  let current = null;

  (items || []).forEach((item) => {
    if (item?.delete) return;
    if (isGroupRow(item)) {
      current = { header: item, children: [], nestedBlocks: [] };
      flat.push(current);
    } else if (current) {
      current.children.push(item);
    } else {
      flat.push({ header: null, children: [item], nestedBlocks: [] });
    }
  });

  const nested = [];
  let openRoot = null;
  flat.forEach((block) => {
    const calcId = Number(read(block.header, "calculation_id"));
    const isRoot =
      block.header &&
      Number.isFinite(calcId) &&
      (rootSet.size === 0 || rootSet.has(calcId));

    if (isRoot) {
      openRoot = {
        header: block.header,
        children: [...block.children],
        nestedBlocks: [],
      };
      nested.push(openRoot);
    } else if (openRoot && block.header) {
      openRoot.nestedBlocks.push(block);
    } else if (openRoot && !block.header) {
      openRoot.children.push(...block.children);
    } else {
      openRoot = null;
      nested.push({ ...block, nestedBlocks: block.nestedBlocks || [] });
    }
  });
  return nested;
}

function transformBlocks(blocks, rules, rootSet) {
  return blocks.flatMap((block) => transformBlock(block, rules, rootSet));
}

function isText(row) {
  return (row?.item_type || row?.type) === "text";
}

function withProductInfo(row, showProductInfo) {
  if (showProductInfo || isText(row) || isGroupRow(row)) return row;
  if (!row?.description) return row;
  return { ...row, description: "" };
}

function transformBlock(block, rules, rootSet) {
  const { header, children = [], nestedBlocks = [] } = block;
  if (!header) {
    return children
      .filter(visible)
      .map((row) => withProductInfo(row, rules.defaults.show_product_info));
  }

  const mode = modeFor(rules, header, rootSet);
  if (mode === "hide") return [];

  const showProductInfo = rules.defaults.show_product_info;

  if (mode === "sum_only") {
    // Keep customer text/note rows — sum_only collapses priced lines only.
    const noteRows = children.filter((row) => isText(row) && visible(row));
    const pricedChildren = children.filter((row) => !isText(row));
    const nestedRows = transformBlocks(nestedBlocks, rules, rootSet);
    const nestedNotes = nestedRows.filter(isText);
    const nestedPriced = nestedRows.filter((row) => !isText(row));
    const total =
      pricedChildren.filter(visible).reduce((sum, row) => sum + lineAmount(row), 0) +
      nestedPriced.reduce((sum, row) => sum + lineAmount(row), 0);
    const override = rules.rules.find((rule) => rule.node_key === nodeKey(header))
      ?.title_override;
    return [
      {
        ...header,
        title: override || header.title,
        quantity: 1,
        sales_price: total,
        discount: 0,
        surcharge: 0,
        item_type: "manual",
        group_row: false,
        presentation_mode: "sum_only",
      },
      ...noteRows,
      ...nestedNotes,
    ];
  }

  return [
    { ...header, presentation_mode: mode },
    ...children
      .filter(visible)
      .map((row) => withProductInfo(row, showProductInfo)),
    ...transformBlocks(nestedBlocks, rules, rootSet),
  ];
}

function applyFieldVisibility(row, rules) {
  if (isGroupRow(row) || isText(row)) return row;
  const nodeKey = itemNodeKey(row);
  const show = rules.defaults.show_fields || {};
  const hidden = (rules.rules || []).find((rule) => rule.node_key === nodeKey)
    ?.hidden_fields || [];
  const hide = (field) => show[field] === false || hidden.includes(field);
  const next = { ...row };
  if (hide("description")) next.description = "";
  if (hide("quantity")) {
    next._hide_quantity = true;
  }
  if (hide("unit")) {
    next._hide_unit = true;
    next.unit = null;
    next.unit_id = null;
  }
  if (hide("unit_price")) {
    next._hide_unit_price = true;
  }
  if (hide("product_image")) {
    next.product_image_url = null;
    next.show_image = false;
  }
  if (hide("product_url")) {
    next.product_url = null;
    next.show_url = false;
  }
  return next;
}

function itemHiddenByRule(row, rules) {
  const key = itemNodeKey(row);
  if (!key) return false;
  return (rules.rules || []).some(
    (rule) => rule.node_key === key && rule.mode === "hide"
  );
}

/** Second pass: notes + per-line/per-field visibility on already-built rows. */
export function applyDocumentVisibility(items = [], rules = {}) {
  const normalized = normalizeRules(rules);
  const includeNotes = notesIncluded(normalized);
  return (items || [])
    .filter((row) => {
      if (!row || row.delete) return false;
      if (itemHiddenByRule(row, normalized)) return false;
      if (!includeNotes && isText(row)) return false;
      return true;
    })
    .map((row) => applyFieldVisibility(row, normalized));
}

export function applyPresentationRules(
  items = [],
  rules = {},
  { rootCalculationIds = [] } = {}
) {
  const normalized = normalizeRules(rules);
  const rootSet = new Set((rootCalculationIds || []).map((id) => Number(id)));
  const blocks = buildBlocks(items, rootCalculationIds);
  return applyDocumentVisibility(
    transformBlocks(blocks, normalized, rootSet),
    normalized
  );
}
